import { z } from 'zod'
import { api } from '@/shared/api/client'
import { syntheticTotal } from '@/shared/api/pageResult'
import { instantSchema, moneySchema, toMoney, uuidSchema } from '@/shared/api/schemas'
import { formatDate } from '@/shared/lib/format'
import type { Money } from '@/shared/lib/money'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'
import {
  actorRefSchema,
  branchRefSchema,
  pageOf,
  type ActorRef,
  type BranchRef,
  type ReceiptCompany,
} from '@/features/cashdesk/cashdeskApi'
import { discountSchema, toDiscount, type Discount } from '@/features/cashdesk/salesApi'

export const INVOICE_STATES = ['DRAFT', 'ISSUED', 'CANCELLED'] as const
export const INVOICE_STATUSES = ['DRAFT', 'ISSUED', 'PARTIALLY_PAID', 'PAID', 'OVERDUE', 'CANCELLED'] as const
export const INVOICE_LINE_KINDS = [
  'PACKAGE',
  'INSTALLATION',
  'EQUIPMENT',
  'SERVICE',
  'OTHER',
  'PREVIOUS_BALANCE',
] as const
export const ALLOCATION_KINDS = ['ISSUE_BALANCE', 'RECEIPT', 'RECEIPT_REVERSAL'] as const

export type InvoiceState = (typeof INVOICE_STATES)[number]
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number]
export type InvoiceLineKind = (typeof INVOICE_LINE_KINDS)[number]

export const INVOICE_STATUS_COLOR: Record<InvoiceStatus, string> = {
  DRAFT: 'default',
  ISSUED: 'blue',
  PARTIALLY_PAID: 'gold',
  PAID: 'green',
  OVERDUE: 'red',
  CANCELLED: 'default',
}

export const NEW_SALE_KINDS: readonly InvoiceLineKind[] = ['INSTALLATION', 'EQUIPMENT', 'SERVICE', 'OTHER']

export function defaultCategoryCode(kind: InvoiceLineKind): 'INTERNET' | 'EQUIPMENT' | 'OTHER' {
  if (kind === 'PACKAGE' || kind === 'PREVIOUS_BALANCE') return 'INTERNET'
  return kind === 'EQUIPMENT' ? 'EQUIPMENT' : 'OTHER'
}

export const INVOICE_TEXT_MAX = 500
export const INVOICE_DESCRIPTION_MAX = 200
export const DEFAULT_DUE_DAYS = 14

const customerSchema = z.object({
  subscriberId: uuidSchema,
  customerId: z.string().nullish(),
  login: z.string(),
  name: z.string().nullish(),
  phone: z.string().nullish(),
  address: z.string().nullish(),
  center: z.string().nullish(),
  visp: z.string().nullish(),
})

const invoiceSchema = z.object({
  id: uuidSchema,
  invoiceNo: z.string().nullish(),
  state: z.enum(INVOICE_STATES),
  status: z.enum(INVOICE_STATUSES),
  invoiceDate: z.string(),
  dueDate: z.string(),
  customer: customerSchema,
  branch: branchRefSchema,
  subtotal: moneySchema,
  discount: moneySchema,
  total: moneySchema,
  paid: moneySchema,
  outstanding: moneySchema,
  createdAt: instantSchema,
  issuedAt: instantSchema.nullish(),
})

const lineSchema = z.object({
  id: uuidSchema,
  lineNo: z.number(),
  kind: z.enum(INVOICE_LINE_KINDS),
  description: z.string().nullish(),
  quantity: z.number(),
  unitPrice: moneySchema,
  amount: moneySchema,
  grantId: uuidSchema.nullish(),
  categoryId: uuidSchema.nullish(),
  itemId: uuidSchema.nullish(),
  warehouseId: uuidSchema.nullish(),
  stockMovementId: uuidSchema.nullish(),
})

const detailSchema = invoiceSchema.extend({
  lines: z.array(lineSchema),
  categoryId: uuidSchema.nullish(),
  notes: z.string().nullish(),
  discountReason: z.string().nullish(),
  discountRequest: discountSchema.nullish(),
  company: z
    .object({
      name: z.string().nullish(),
      address: z.string().nullish(),
      phone: z.string().nullish(),
      email: z.string().nullish(),
      website: z.string().nullish(),
      logoUrl: z.string().nullish(),
    })
    .nullish(),
  createdBy: actorRefSchema,
  issuedBy: actorRefSchema.nullish(),
  cancelledBy: actorRefSchema.nullish(),
  cancelledAt: instantSchema.nullish(),
  cancelReason: z.string().nullish(),
  events: z.array(
    z.object({
      event: z.string(),
      actor: actorRefSchema.nullish(),
      at: instantSchema,
      detail: z.record(z.string(), z.unknown()).nullish(),
    }),
  ),
  allocations: z.array(
    z.object({
      kind: z.enum(ALLOCATION_KINDS),
      amount: moneySchema,
      receiptId: uuidSchema.nullish(),
      receiptNo: z.string().nullish(),
      at: instantSchema,
    }),
  ),
  printCount: z.number(),
})

export interface InvoiceCustomer {
  subscriberId: string
  customerId: string | null
  login: string
  name: string | null
  phone: string | null
  address: string | null
  center: string | null
  visp: string | null
}

export interface Invoice {
  id: string
  invoiceNo: string | null
  state: InvoiceState
  status: InvoiceStatus
  invoiceDate: string
  dueDate: string
  customer: InvoiceCustomer
  branch: BranchRef
  subtotal: Money
  discount: Money
  total: Money
  paid: Money
  outstanding: Money
  createdAt: string
  issuedAt: string | null
}

export interface InvoiceLine {
  id: string
  lineNo: number
  kind: InvoiceLineKind
  description: string | null
  quantity: number
  unitPrice: Money
  amount: Money
  grantId: string | null
  categoryId: string | null
  itemId: string | null
  warehouseId: string | null
  stockMovementId: string | null
}

export interface InvoiceEvent {
  event: string
  actor: ActorRef | null
  at: string
  detail: Record<string, unknown>
}

export interface InvoiceAllocation {
  kind: (typeof ALLOCATION_KINDS)[number]
  amount: Money
  receiptId: string | null
  receiptNo: string | null
  at: string
}

export interface InvoiceDetail extends Invoice {
  lines: InvoiceLine[]
  categoryId: string | null
  notes: string | null
  discountReason: string | null
  discountRequest: Discount | null
  company: ReceiptCompany
  createdBy: ActorRef
  issuedBy: ActorRef | null
  cancelledBy: ActorRef | null
  cancelledAt: string | null
  cancelReason: string | null
  events: InvoiceEvent[]
  allocations: InvoiceAllocation[]
  printCount: number
}

function toInvoice(raw: z.infer<typeof invoiceSchema>): Invoice {
  const c = raw.customer
  return {
    id: raw.id,
    invoiceNo: raw.invoiceNo ?? null,
    state: raw.state,
    status: raw.status,
    invoiceDate: raw.invoiceDate,
    dueDate: raw.dueDate,
    customer: {
      subscriberId: c.subscriberId,
      customerId: c.customerId ?? null,
      login: c.login,
      name: c.name ?? null,
      phone: c.phone ?? null,
      address: c.address ?? null,
      center: c.center ?? null,
      visp: c.visp ?? null,
    },
    branch: raw.branch,
    subtotal: toMoney(raw.subtotal),
    discount: toMoney(raw.discount),
    total: toMoney(raw.total),
    paid: toMoney(raw.paid),
    outstanding: toMoney(raw.outstanding),
    createdAt: raw.createdAt,
    issuedAt: raw.issuedAt ?? null,
  }
}

export function parseInvoiceDetail(data: unknown): InvoiceDetail {
  const raw = detailSchema.parse(data)
  const company = raw.company ?? {}
  return {
    ...toInvoice(raw),
    lines: raw.lines.map((line) => ({
      id: line.id,
      lineNo: line.lineNo,
      kind: line.kind,
      description: line.description ?? null,
      quantity: line.quantity,
      unitPrice: toMoney(line.unitPrice),
      amount: toMoney(line.amount),
      grantId: line.grantId ?? null,
      categoryId: line.categoryId ?? null,
      itemId: line.itemId ?? null,
      warehouseId: line.warehouseId ?? null,
      stockMovementId: line.stockMovementId ?? null,
    })),
    categoryId: raw.categoryId ?? null,
    notes: raw.notes ?? null,
    discountReason: raw.discountReason ?? null,
    discountRequest: raw.discountRequest ? toDiscount(raw.discountRequest) : null,
    company: {
      name: company.name ?? null,
      address: company.address ?? null,
      phone: company.phone ?? null,
      email: company.email ?? null,
      website: company.website ?? null,
      logoUrl: company.logoUrl ?? null,
    },
    createdBy: raw.createdBy,
    issuedBy: raw.issuedBy ?? null,
    cancelledBy: raw.cancelledBy ?? null,
    cancelledAt: raw.cancelledAt ?? null,
    cancelReason: raw.cancelReason ?? null,
    events: raw.events.map((e) => ({ event: e.event, actor: e.actor ?? null, at: e.at, detail: e.detail ?? {} })),
    allocations: raw.allocations.map((a) => ({
      kind: a.kind,
      amount: toMoney(a.amount),
      receiptId: a.receiptId ?? null,
      receiptNo: a.receiptNo ?? null,
      at: a.at,
    })),
    printCount: raw.printCount,
  }
}

export function parseInvoicePage(data: unknown) {
  const parsed = pageOf(invoiceSchema).parse(data)
  return { rows: parsed.rows.map(toInvoice), hasNext: parsed.hasNext }
}

export interface InvoiceFilter {
  state?: InvoiceState | null
  subscriberId?: string | null
  branchId?: string | null
}

export function fetchInvoicePage(
  filter: InvoiceFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<Invoice>> {
  return async (state, signal) => {
    const response = await api.get<unknown>('/api/invoices', {
      signal,
      params: {
        ...(state.search.trim() ? { q: state.search.trim() } : {}),
        ...(filter.state ? { state: filter.state } : {}),
        ...(filter.subscriberId ? { subscriberId: filter.subscriberId } : {}),
        ...(filter.branchId ? { branchId: filter.branchId } : {}),
        page: state.page - 1,
        size: state.pageSize,
      },
    })
    const parsed = parseInvoicePage(response.data)
    return {
      items: parsed.rows,
      total: syntheticTotal(state.page, state.pageSize, parsed.rows.length, parsed.hasNext),
    }
  }
}

export async function fetchInvoice(id: string, signal?: AbortSignal): Promise<InvoiceDetail> {
  const response = await api.get<unknown>(`/api/invoices/${id}`, { ...(signal ? { signal } : {}) })
  return parseInvoiceDetail(response.data)
}

const billableSchema = z.object({
  sales: z.array(
    z.object({
      grantId: uuidSchema,
      planId: uuidSchema.nullish(),
      planName: z.string().nullish(),
      charged: moneySchema,
      soldAt: instantSchema,
      state: z.string().nullish(),
    }),
  ),
  outstanding: moneySchema,
})

export interface BillableSale {
  grantId: string
  planId: string | null
  planName: string | null
  charged: Money
  soldAt: string
  state: string | null
}

export interface Billable {
  sales: BillableSale[]
  outstanding: Money
}

export function parseBillable(data: unknown): Billable {
  const raw = billableSchema.parse(data)
  return {
    sales: raw.sales.map((s) => ({
      grantId: s.grantId,
      planId: s.planId ?? null,
      planName: s.planName ?? null,
      charged: toMoney(s.charged),
      soldAt: s.soldAt,
      state: s.state ?? null,
    })),
    outstanding: toMoney(raw.outstanding),
  }
}

export async function fetchBillable(subscriberId: string, signal?: AbortSignal): Promise<Billable> {
  const response = await api.get<unknown>('/api/invoices/billable', {
    ...(signal ? { signal } : {}),
    params: { subscriberId },
  })
  return parseBillable(response.data)
}

export interface InvoiceLineDraft {
  kind: InvoiceLineKind
  description: string | null
  quantity: number
  unitPrice: number
  grantId: string | null
  categoryId: string | null
  itemId: string | null
  warehouseId: string | null
}

export interface InvoiceDraft {
  subscriberId: string
  branchId: string | null
  invoiceDate: string
  dueDate: string
  address: string | null
  notes: string | null
  discount: number | null
  discountReason: string | null
  lines: InvoiceLineDraft[]
}

function draftBody(draft: InvoiceDraft) {
  return {
    subscriberId: draft.subscriberId,
    ...(draft.branchId ? { branchId: draft.branchId } : {}),
    invoiceDate: draft.invoiceDate,
    dueDate: draft.dueDate,
    address: draft.address,
    notes: draft.notes,
    discount: draft.discount && draft.discount > 0 ? draft.discount : null,
    discountReason: draft.discount && draft.discount > 0 ? draft.discountReason : null,
    lines: draft.lines.map((line) => ({
      kind: line.kind,
      description: line.description,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      grantId: line.kind === 'PACKAGE' ? line.grantId : null,
      categoryId: line.categoryId,
      itemId: line.kind === 'EQUIPMENT' ? line.itemId : null,
      warehouseId: line.kind === 'EQUIPMENT' ? line.warehouseId : null,
    })),
  }
}

export async function createInvoice(draft: InvoiceDraft, idempotencyKey: string): Promise<InvoiceDetail> {
  const response = await api.post<unknown>('/api/invoices', draftBody(draft), {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
  return parseInvoiceDetail(response.data)
}

export async function updateInvoice(id: string, draft: InvoiceDraft): Promise<InvoiceDetail> {
  const response = await api.put<unknown>(`/api/invoices/${id}`, draftBody(draft))
  return parseInvoiceDetail(response.data)
}

export async function requestInvoiceDiscount(id: string): Promise<InvoiceDetail> {
  const response = await api.post<unknown>(`/api/invoices/${id}/discount-approval`)
  return parseInvoiceDetail(response.data)
}

export async function issueInvoice(id: string, allowCredit: boolean): Promise<InvoiceDetail> {
  const response = await api.post<unknown>(`/api/invoices/${id}/issue`, { allowCredit })
  return parseInvoiceDetail(response.data)
}

export async function cancelInvoice(id: string, reason: string): Promise<InvoiceDetail> {
  const response = await api.post<unknown>(`/api/invoices/${id}/cancel`, { reason })
  return parseInvoiceDetail(response.data)
}

export async function recordInvoicePrint(id: string): Promise<{ copy: boolean; printNo: number }> {
  const response = await api.post<unknown>(`/api/invoices/${id}/prints`)
  return z.object({ copy: z.boolean(), printNo: z.number() }).parse(response.data)
}

export function lineAmount(line: Pick<InvoiceLineDraft, 'quantity' | 'unitPrice'>): number {
  return line.quantity * line.unitPrice
}

export function invoiceTotals(lines: readonly Pick<InvoiceLineDraft, 'quantity' | 'unitPrice'>[], discount: number) {
  const subtotal = lines.reduce((sum, line) => sum + lineAmount(line), 0)
  return { subtotal, total: subtotal - discount }
}

export function formatInvoiceDay(day: string | null | undefined): string {
  return day ? formatDate(`${day}T12:00:00Z`) : formatDate(null)
}
